import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';

import { NEW_TU_DROPPABLE_ID } from '../../constants';
import { UnitTypeOption } from '../../state/packingListReducer';
import { LineItemEntry, LineItemId, TransportUnitId, TransportUnitNode } from '../../types';
import { TransportUnitCard } from '../TransportUnitCard/TransportUnitCard';

type PackingListSectionProps = {
  transportUnits: Array<TransportUnitNode>;
  lineItemsById: Record<LineItemId, LineItemEntry>;
  unitTypeOptions: Array<UnitTypeOption>;
  onChangeUnitType: (tuId: TransportUnitId, unitType: string) => void;
  onDeleteTransportUnit: (tuId: TransportUnitId) => void;
  onRemoveItem: (lineItemId: LineItemId, tuId: TransportUnitId) => void;
  selectedPlacedKeys: Set<string>;
  onTogglePlaced: (tuId: TransportUnitId, lineItemId: LineItemId) => void;
  onMoveToItem: (tuId: TransportUnitId, lineItemId: LineItemId) => void;
  onEditItemQuantity: (tuId: TransportUnitId, lineItemId: LineItemId) => void;
};

/**
 * The new-transport-unit dropzone label is selected with a ternary inside t();
 * enumerate both branches so the i18next-parser keeps them.
 *
 * i18n keys
 * t('webedi:packing_list.first_transport_unit')
 * t('webedi:packing_list.new_transport_unit')
 */
export const PackingListSection = ({
  transportUnits,
  lineItemsById,
  unitTypeOptions,
  onChangeUnitType,
  onDeleteTransportUnit,
  onRemoveItem,
  selectedPlacedKeys,
  onTogglePlaced,
  onMoveToItem,
  onEditItemQuantity,
}: PackingListSectionProps) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: NEW_TU_DROPPABLE_ID });
  return (
    <div className="flex-1 space-y-4 overflow-y-auto p-6">
      {transportUnits.length === 0
        ? null
        : transportUnits.map((node, index) => (
            <TransportUnitCard
              key={node.id}
              node={node}
              position={String(index + 1)}
              level={1}
              lineItemsById={lineItemsById}
              unitTypeOptions={unitTypeOptions}
              onChangeUnitType={onChangeUnitType}
              onDelete={onDeleteTransportUnit}
              onRemoveItem={onRemoveItem}
              selectedPlacedKeys={selectedPlacedKeys}
              onTogglePlaced={onTogglePlaced}
              onMoveToItem={onMoveToItem}
              onEditItemQuantity={onEditItemQuantity}
            />
          ))}
      <div
        ref={setNodeRef}
        className={`rounded-lg border border-dashed px-4 py-3 text-center text-sm ${isOver ? 'border-procuros-green-600 bg-green-50' : 'border-gray-300 text-gray-400'}`}
      >
        {t(
          transportUnits.length === 0
            ? 'webedi:packing_list.first_transport_unit'
            : 'webedi:packing_list.new_transport_unit',
        )}
      </div>
    </div>
  );
};
